import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetServiceCategoriesQuery,
  useDeleteServiceCategoryMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const ServicesCategories = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t, isArabic } = useTranslation();

  // RTK Query hooks
  const { data: categoriesData, isLoading: loading } =
    useGetServiceCategoriesQuery();
  const [deleteCategory] = useDeleteServiceCategoryMutation();

  const categories = categoriesData?.categories || [];
  const [statusMessage, setStatusMessage] = useState("");
  const messageType = useMessageType(statusMessage);

  const handleDelete = async (id) => {
    if (!window.confirm(t("servicesDashboard.deleteConfirmMessage"))) {
      return;
    }

    try {
      await deleteCategory(id).unwrap();
      setStatusMessage(t("servicesDashboard.categoryDeletedSuccessfully"));
    } catch (error) {
      console.error("Error deleting category:", error);
      setStatusMessage(
        error.data?.message || t("servicesDashboard.errorDeletingCategory")
      );
    }
  };

  const handleAddNew = () => {
    navigate("/dashboard/services-categories/add");
  };

  const handleEdit = (categoryId) => {
    navigate(`/dashboard/services-categories/edit/${categoryId}`);
  };

  if (loading) {
    return (
      <div className="services-categories">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("servicesDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="services-categories">
        <div className="auth-required">
          <h2>{t("servicesDashboard.authenticationRequired")}</h2>
          <p>{t("servicesDashboard.pleaseLoginToManage")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("servicesDashboard.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="services-categories">
      <div className="page-header">
        <div className="header-content">
          <h1>{t("servicesDashboard.servicesCategories")}</h1>
          <p>{t("servicesDashboard.manageServiceCategories")}</p>
        </div>
        <div className="header-actions">
          <button type="button" className="add-btn" onClick={handleAddNew}>
            <Icon name="plus" size="16px" />
            {t("servicesDashboard.addNewCategory")}
          </button>
        </div>
      </div>

      <StatusMessage
        message={statusMessage}
        type={messageType}
        show={!!statusMessage}
        autoClose={messageType === "success"}
        autoCloseDelay={5000}
        onClose={() => setStatusMessage("")}
      />

      <div className="categories-container">
        {categories.length > 0 ? (
          <div className="categories-table-container">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>{t("servicesDashboard.title")}</th>
                  <th>{t("servicesDashboard.status")}</th>
                  <th>{t("servicesDashboard.created")}</th>
                  <th>{t("servicesDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td className="title-cell">
                      {isArabic && category.title_ar
                        ? category.title_ar
                        : category.title}
                    </td>
                    <td className="status-cell">
                      <span
                        className={`status-badge ${
                          category.is_active ? "active" : "inactive"
                        }`}
                      >
                        {category.is_active
                          ? t("servicesDashboard.active")
                          : t("servicesDashboard.inactive")}
                      </span>
                    </td>
                    <td className="date-cell">
                      {new Date(category.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(category.id)}
                          title={t("servicesDashboard.editCategory")}
                        >
                          <Icon name="edit" size="14px" />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(category.id)}
                          title={t("servicesDashboard.deleteCategory")}
                        >
                          <Icon name="trash" size="14px" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-categories">
            <div className="empty-content">
              <Icon name="folder-open" size="48px" />
              <h3>{t("servicesDashboard.noCategoriesFound")}</h3>
              <p>{t("addEditCategory.createNewCategory")}</p>
              <button className="add-first-category-btn" onClick={handleAddNew}>
                <Icon name="plus" size="16px" />
                {t("servicesDashboard.addNewCategory")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesCategories;
